import { useEffect, useState } from 'react'
import { Container, Row, Col, Card, Table, Button, Modal, Form, Alert, InputGroup } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getEvaluations, addEvaluation } from '../../../actions/evaluationActions'
import { getStudents } from '../../../actions/studentActions'
import Loader from '../../../components/loaders/Loader'
import { toastSuccess, toastError } from '../../../components/swal/toast'

export default function EvaluationList() {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { evaluations, loading, evaluationError } = useSelector(state => state.evaluations)
    const { students } = useSelector(state => state.students)
    const [showModal, setShowModal] = useState(false)
    const [form, setForm] = useState({ studentId: '', type: 'continuous', score: '', maxScore: '10', comments: '', period: '', speaking: '', listening: '', reading: '', writing: '', useOfEnglish: '' })
    const [search, setSearch] = useState('')

    const competenceFields = [
        { key: 'speaking', label: t('teacher.students.speaking') },
        { key: 'listening', label: t('teacher.students.listening') },
        { key: 'reading', label: t('teacher.students.reading') },
        { key: 'writing', label: t('teacher.students.writing') },
        { key: 'useOfEnglish', label: t('teacher.students.useofenglish') }
    ]

    useEffect(() => {
        dispatch(getEvaluations())
        dispatch(getStudents())
    }, [dispatch])

    if (loading && (!evaluations || evaluations.length === 0)) return <Loader />

    if (evaluationError) return (
        <Container className='my-3 p-5'>
            <Alert variant='danger'>{evaluationError}</Alert>
        </Container>
    )

    const filtered = (evaluations || []).filter(ev => {
        if (!search) return true
        const q = search.toLowerCase()
        return (ev.studentName || '').toLowerCase().includes(q) ||
               (ev.type || '').toLowerCase().includes(q) ||
               (ev.period || '').toLowerCase().includes(q) ||
               (ev.comments || '').toLowerCase().includes(q)
    })

    const typeLabel = (type) => {
        switch (type) {
            case 'continuous': return t('teacher.evaluations.continuous')
            case 'competence': return t('teacher.evaluations.competence')
            case 'exam': return t('teacher.evaluations.exam')
            case 'mock_exam': return t('teacher.evaluations.mockexam')
            default: return type
        }
    }

    const handleSubmit = () => {
        if (!form.studentId) {
            toastError(t('teacher.evaluations.selectStudent'))
            return
        }
        if (!form.score) {
            toastError(t('teacher.evaluations.enterScore'))
            return
        }
        dispatch(addEvaluation({
            studentId: form.studentId,
            type: form.type,
            score: Number(form.score),
            maxScore: Number(form.maxScore) || 10,
            comments: form.comments,
            period: form.period,
            competences: form.type === 'competence'
                ? {
                    speaking: form.speaking === '' ? undefined : Number(form.speaking),
                    listening: form.listening === '' ? undefined : Number(form.listening),
                    reading: form.reading === '' ? undefined : Number(form.reading),
                    writing: form.writing === '' ? undefined : Number(form.writing),
                    useOfEnglish: form.useOfEnglish === '' ? undefined : Number(form.useOfEnglish)
                }
                : undefined
        }))
        setShowModal(false)
        setForm({ studentId: '', type: 'continuous', score: '', maxScore: '10', comments: '', period: '', speaking: '', listening: '', reading: '', writing: '', useOfEnglish: '' })
        toastSuccess(t('teacher.evaluations.evaluationSaved'))
    }

    return (
        <Container className='my-3 p-5'>
            <Row>
                <Col className='d-flex justify-content-between align-items-center'>
                    <div>
                        <h3 className='fw-bold mb-1'>{t('teacher.evaluations.title')}</h3>
                        <p className='text-muted mb-0'>{evaluations?.length || 0} {t('teacher.evaluations.evaluationcount')}</p>
                    </div>
                    <div className='d-flex gap-2'>
                        <Link to='/teacher/evaluations/progress' className='btn btn-outline-secondary rounded-3'>
                            {t('teacher.evaluations.progressReport')}
                        </Link>
                        <Button variant='primary' className='rounded-3' onClick={() => setShowModal(true)}>
                            {t('teacher.evaluations.newevaluation')}
                        </Button>
                    </div>
                </Col>
            </Row>

            <Row className='mt-3 mb-3'>
                <Col md={6} lg={4}>
                    <InputGroup>
                        <InputGroup.Text className='bg-white border-end-0'>
                            <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#9ca3af' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                                <circle cx='11' cy='11' r='8' /><line x1='21' y1='21' x2='16.65' y2='16.65' />
                            </svg>
                        </InputGroup.Text>
                        <Form.Control
                            placeholder={t('teacher.evaluations.searchPlaceholder')}
                            className='border-start-0'
                            style={{ borderRadius: '0 10px 10px 0' }}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </InputGroup>
                </Col>
            </Row>

            <Row>
                <Col>
                    <Card className='border-0 shadow-sm rounded-4'>
                        <Card.Body className='p-0'>
                            {filtered.length > 0
                                ? <Table hover responsive className='mb-0'>
                                    <thead className='bg-light'>
                                        <tr>
                                            <th className='fw-semibold' style={{ fontSize: '13px' }}>{t('teacher.evaluations.student')}</th>
                                            <th className='fw-semibold' style={{ fontSize: '13px' }}>{t('teacher.evaluations.type')}</th>
                                            <th className='fw-semibold' style={{ fontSize: '13px' }}>{t('teacher.evaluations.score')}</th>
                                            <th className='fw-semibold' style={{ fontSize: '13px' }}>{t('teacher.evaluations.period')}</th>
                                            <th className='fw-semibold' style={{ fontSize: '13px' }}>{t('teacher.evaluations.date')}</th>
                                            <th className='fw-semibold' style={{ fontSize: '13px' }}>{t('teacher.evaluations.comments')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map(ev => (
                                            <tr key={ev._id || ev.id}>
                                                <td className='fw-semibold'>{ev.studentName}</td>
                                                <td><span className='badge bg-light text-dark border'>{typeLabel(ev.type)}</span></td>
                                                <td><span className='fw-bold'>{ev.score}</span><span className='text-muted'>/{ev.maxScore}</span></td>
                                                <td>{ev.period || '-'}</td>
                                                <td className='text-muted'>{ev.date ? new Date(ev.date).toLocaleDateString() : '-'}</td>
                                                <td className='text-muted' style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.comments || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                : <div className='p-4 text-center'>
                                    <p className='text-muted mb-0'>
                                        {search ? t('global.results') : t('teacher.evaluations.norevaluations')}
                                    </p>
                                </div>
                            }
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={showModal} onHide={() => setShowModal(false)} size='lg'>
                <Modal.Header closeButton>
                    <Modal.Title>{t('teacher.evaluations.newevaluation')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className='mb-3'>
                            <Form.Label>{t('teacher.evaluations.student')} *</Form.Label>
                            <Form.Select style={{ borderRadius: '10px' }}
                                value={form.studentId} onChange={e => setForm({ ...form, studentId: e.target.value })}>
                                <option value=''>{t('teacher.evaluations.studentselect')}</option>
                                {students.map(s => (
                                    <option key={s._id || s.id} value={s._id || s.id}>{s.name}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className='mb-3'>
                            <Form.Label>{t('teacher.evaluations.type')}</Form.Label>
                            <Form.Select style={{ borderRadius: '10px' }}
                                value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                                <option value='continuous'>{t('teacher.evaluations.continuous')}</option>
                                <option value='competence'>{t('teacher.evaluations.competence')}</option>
                                <option value='exam'>{t('teacher.evaluations.exam')}</option>
                                <option value='mock_exam'>{t('teacher.evaluations.mockexam')}</option>
                            </Form.Select>
                        </Form.Group>
                        <Row>
                            <Col md={6}>
                                <Form.Group className='mb-3'>
                                    <Form.Label>{t('teacher.evaluations.score')} *</Form.Label>
                                    <Form.Control type='number' min='0' style={{ borderRadius: '10px' }}
                                        value={form.score} onChange={e => setForm({ ...form, score: e.target.value })} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className='mb-3'>
                                    <Form.Label>{t('teacher.evaluations.max')}</Form.Label>
                                    <Form.Control type='number' min='1' style={{ borderRadius: '10px' }}
                                        value={form.maxScore} onChange={e => setForm({ ...form, maxScore: e.target.value })} />
                                </Form.Group>
                            </Col>
                        </Row>
                        {form.type === 'competence' && (
                            <div className='p-3 mb-3 bg-light rounded-3'>
                                <p className='fw-semibold mb-2' style={{ fontSize: '14px' }}>{t('teacher.evaluations.competence')}</p>
                                <Row>
                                    {competenceFields.map(f => (
                                        <Col key={f.key} xs={6} md={4}>
                                            <Form.Group className='mb-2'>
                                                <Form.Label style={{ fontSize: '13px' }}>{f.label}</Form.Label>
                                                <Form.Control type='number' min='0' max='10' step='0.5' style={{ borderRadius: '10px' }}
                                                    value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
                                            </Form.Group>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        )}
                        <Form.Group className='mb-3'>
                            <Form.Label>{t('teacher.evaluations.period')}</Form.Label>
                            <Form.Select style={{ borderRadius: '10px' }}
                                value={form.period} onChange={e => setForm({ ...form, period: e.target.value })}>
                                <option value=''>{t('teacher.evaluations.periodselect')}</option>
                                <option value='Trim1'>{t('teacher.evaluations.trimestre1')}</option>
                                <option value='Trim2'>{t('teacher.evaluations.trimestre2')}</option>
                                <option value='Trim3'>{t('teacher.evaluations.trimestre3')}</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className='mb-3'>
                            <Form.Label>{t('teacher.evaluations.comments')}</Form.Label>
                            <Form.Control as='textarea' rows={3} style={{ borderRadius: '10px' }}
                                value={form.comments} onChange={e => setForm({ ...form, comments: e.target.value })} />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' onClick={() => setShowModal(false)}>{t('global.cancel')}</Button>
                    <Button variant='primary' onClick={handleSubmit}>{t('global.register')}</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    )
}
